import * as React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

// Utils
import { formatDate } from '@src/shared/src/util/date';
import { head } from '@src/shared/src/util/general';
import { getProductName } from '@src/shared/src/util/trips';

// Constants
import { DATE_FORMAT_TYPES, VEHICLE_TYPES } from '@src/shared/src/const/app';

// Models
import { LegModel, TransportSegmentModel } from '@src/shared/src/models';

// Components
import { Collapse } from '@toolkit/ui/Collapse';
import { BookingInfoItem } from '@src/components/BookingInfoItem';
import { TransportSegmentIcon } from '@src/components/TransportSegmentIcon';

// Styles
import '../styles/TransitInformation.scss';

type Props = {
  leg: LegModel;
  waitingMinutes: number;
};

const TransitInformation: React.FC<Props> = (props) => {
  const { leg, waitingMinutes } = props;
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(false);

  const segments: TransportSegmentModel[] = leg.legOption.transportSegments;
  // A transfer block is treated as one mode for the header subtitle; taxi transfers aren't
  // currently mixed with other modes, so the first segment's vehicle is representative.
  const isTaxi = head(segments)?.vehicles[0] === VEHICLE_TYPES.TAXI;

  return (
    <div className={classNames('transit-information', { 'is--open': isExpanded })}>
      <div className="transit-information-inner">
        <button
          type="button"
          className="transit-information-header"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}>
          <div className="transit-information-header-text">
            <div className="transit-information-header-title">
              {t('TransitInformation.label.transfers', { count: segments.length })}
            </div>
            <div className="transit-information-header-subtitle">
              {isTaxi ? t('TransitInformation.label.taxi') : t('TransitInformation.label.publicTransit')}
            </div>
          </div>
          <i className="material-symbols transit-information-header-icon">expand_more</i>
        </button>

        <Collapse isExpanded={isExpanded}>
          <div className="transit-information-segments">
            {segments.map((transportSegment: TransportSegmentModel) => (
              <div key={transportSegment.id}>
                <BookingInfoItem
                  depName={transportSegment.depName}
                  depTime={formatDate(transportSegment.depAt, DATE_FORMAT_TYPES.LONG_TIME)}
                  arrName={transportSegment.arrName}
                  arrTime={formatDate(transportSegment.arrAt, DATE_FORMAT_TYPES.LONG_TIME)}
                />
                <div className="transit-information-segment-product">
                  <TransportSegmentIcon transportSegment={transportSegment} />
                  {getProductName(transportSegment)}
                </div>
              </div>
            ))}
          </div>
          <div className="transit-information-footer">
            <strong>{waitingMinutes} min</strong>{' '}
            <span>{t('TransitInformation.label.transferWaitingTime')}</span>
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default TransitInformation;
